import * as React from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Divider,
  Link,
} from "@mui/material";
import SEO from "../components/common/SEO";
import "./PrivacyPage.css";

export default function PrivacyPage() {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Read Loops & Purls' privacy policy. Learn how we handle your data — we respect your privacy and don't require accounts for pattern creation."
        keywords="privacy policy, data protection, user privacy, knitting app privacy"
      />
      <Box component="main" className="fade-in-up">
      <Container maxWidth="lg" className="privacy-container">
        {/* Header */}
        <Box className="center mb-4 mt-4">
          <Typography variant="h2" color="primary">
            Privacy Policy
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            className="mt-1"
          >
            Last updated: {new Date().toLocaleDateString()}
          </Typography>
          <Box component="hr" className="hr-accent" aria-hidden="true" />
        </Box>

        {/* Content */}
        <Paper
          variant="woven"
          elevation={1}
          className="paper paper--woven mb-4"
        >
          <Box className="privacy-content">
            <section className="mb-4">
              <Typography variant="h5" color="primary" className="mb-2">
                Data Collection
              </Typography>
              <Typography variant="body1" className="mb-2">
                Loops & Purls is designed with privacy in mind. General use of the
                application does not require any personal information. All patterns
                you create are stored locally in your browser, and we do not have
                access to them unless you explicitly choose to share them through the
                Gallery feature.
              </Typography>
              <Typography variant="body1" className="mb-2">
                <strong>Gallery Submissions:</strong> If you choose to submit a pattern
                to the Gallery, you may voluntarily provide the following information:
              </Typography>
              <ul className="mb-2">
                <li>
                  <Typography component="span" variant="body1">
                    Pattern name (required)
                  </Typography>
                </li>
                <li>
                  <Typography component="span" variant="body1">
                    Your name or creator name (optional)
                  </Typography>
                </li>
                <li>
                  <Typography component="span" variant="body1">
                    Email address (optional)
                  </Typography>
                </li>
                <li>
                  <Typography component="span" variant="body1">
                    Tags and notes (optional)
                  </Typography>
                </li>
              </ul>
              <Typography variant="body1">
                All personal information provided in Gallery submissions is optional
                except for the pattern name. We only collect the information you
                choose to provide.
              </Typography>
            </section>

            <Divider className="my-3" />

            <section className="mb-4">
              <Typography variant="h5" color="primary" className="mb-2">
                Usage Information
              </Typography>
              <Typography variant="body1">
                We do not currently collect usage statistics or analytics data. If we
                begin collecting such information in the future, we will update this
                Privacy Policy to reflect those practices.
              </Typography>
            </section>

            <Divider className="my-3" />

            <section className="mb-4">
              <Typography variant="h5" color="primary" className="mb-2">
                Cookies
              </Typography>
              <Typography variant="body1" className="mb-2">
                Loops & Purls may use cookies or similar technologies to enhance
                your experience. These cookies are used for:
              </Typography>
              <ul className="mb-2">
                <li>
                  <Typography component="span" variant="body1">
                    Storing your pattern data locally
                  </Typography>
                </li>
                <li>
                  <Typography component="span" variant="body1">
                    Remembering your preferences
                  </Typography>
                </li>
              </ul>
              <Typography variant="body1">
                You can control cookie settings through your browser preferences.
              </Typography>
            </section>

            <Divider className="my-3" />

            <section className="mb-4">
              <Typography variant="h5" color="primary" className="mb-2">
                Pattern Sharing and Moderation
              </Typography>
              <Typography variant="body1" className="mb-2">
                When you submit a pattern to the Gallery, you may choose to provide
                your name or other identifiable information. All personal information
                you provide is optional and is kept private. Your pattern information
                is stored in our system and used to manage the Gallery.
              </Typography>
              <Typography variant="body1" className="mb-2">
                Only your name (if provided) and pattern information are displayed
                publicly in the Gallery. If you choose to provide an email address, it
                is stored privately and is never displayed publicly. Email addresses
                are used only for verification purposes if you request pattern removal
                or for follow-up communications.
              </Typography>
              <Typography variant="body1">
                Patterns submitted to the Gallery are reviewed before being made
                visible. We reserve the right to remove any pattern that violates our
                content policy or terms of service.
              </Typography>
            </section>

            <Divider className="my-3" />

            <section className="mb-4">
              <Typography variant="h5" color="primary" className="mb-2">
                Data Storage and Third-Party Services
              </Typography>
              <Typography variant="body1" className="mb-2">
                Gallery submission data, including any email addresses you provide,
                is stored in Google Sheets. This data is accessed during the manual
                review and approval process, and when you request changes or removal.
                The application never automatically loads or displays your email
                address back to you or other users.
              </Typography>
              <Typography variant="body1" className="mb-2">
                <strong>Data Retention:</strong> We retain your Gallery submission
                data until you request its removal. We do not automatically delete
                data unless you specifically request it.
              </Typography>
              <Typography variant="body1" className="mb-2">
                <strong>Data Sharing:</strong> We will NEVER sell your data. Your
                information is used solely for Gallery management and to process your
                removal or modification requests. Data is only accessed when
                requested by you.
              </Typography>
            </section>

            <Divider className="my-3" />

            <section className="mb-4">
              <Typography variant="h5" color="primary" className="mb-2">
                Your Rights (GDPR/CCPA)
              </Typography>
              <Typography variant="body1" className="mb-2">
                If you have submitted a pattern to the Gallery, you have the
                following rights regarding your personal data:
              </Typography>
              <ul className="mb-2">
                <li>
                  <Typography component="span" variant="body1">
                    <strong>Right to Access:</strong> You may request a copy of all
                    data associated with your email address by{" "}
                    <Link
                      href="mailto:hadleyswello@gmail.com?subject=Privacy Request: Data Access"
                      color="primary"
                    >
                      emailing us
                    </Link>{" "}
                    from the email address associated with your pattern.
                  </Typography>
                </li>
                <li>
                  <Typography component="span" variant="body1">
                    <strong>Right to Deletion:</strong> You may request deletion of
                    your Gallery submission by{" "}
                    <Link
                      href="mailto:hadleyswello@gmail.com?subject=Privacy Request: Data Deletion"
                      color="primary"
                    >
                      emailing us
                    </Link>{" "}
                    from the email address associated with your pattern. If you email
                    from a different address, you may still request deletion if you can
                    provide proof of pattern ownership or copyright.
                  </Typography>
                </li>
                <li>
                  <Typography component="span" variant="body1">
                    <strong>Right to Data Portability:</strong> You may request your
                    data in a portable format by{" "}
                    <Link
                      href="mailto:hadleyswello@gmail.com?subject=Privacy Request: Data Portability"
                      color="primary"
                    >
                      emailing us
                    </Link>{" "}
                    from the associated email address.
                  </Typography>
                </li>
                <li>
                  <Typography component="span" variant="body1">
                    <strong>Right to Modification:</strong> You may request changes to
                    your Gallery submission (such as updating your name or tags) by{" "}
                    <Link
                      href="mailto:hadleyswello@gmail.com?subject=Privacy Request: Pattern Modification"
                      color="primary"
                    >
                      emailing us
                    </Link>{" "}
                    from the associated email address.
                  </Typography>
                </li>
              </ul>
              <Typography variant="body1" className="mb-2">
                <strong>Name/Username Information:</strong> Names provided in Gallery
                submissions are treated as usernames or monikers for display purposes
                only. You may use your actual name, but it is not required or
                verified. Names are displayed publicly in the Gallery.
              </Typography>
              <Typography variant="body1">
                <strong>Email Purpose:</strong> Email addresses are collected solely
                to allow you to request removal or changes to your Gallery
                submissions. They are never displayed publicly and are only accessed
                when you make a request.
              </Typography>
            </section>

            <Divider className="my-3" />

            <section className="mb-4">
              <Typography variant="h5" color="primary" className="mb-2">
                Local Data Control
              </Typography>
              <Typography variant="body1" className="mb-2">
                For patterns created but not submitted to the Gallery, you have full
                control:
              </Typography>
              <ul className="mb-2">
                <li>
                  <Typography component="span" variant="body1">
                    Your locally stored patterns (you can delete them at any time
                    through your browser settings)
                  </Typography>
                </li>
              </ul>
            </section>

            <Divider className="my-3" />

            <section>
              <Typography variant="h5" color="primary" className="mb-2">
                Contact
              </Typography>
              <Typography variant="body1">
                If you have questions about this Privacy Policy or wish to exercise
                your privacy rights, please{" "}
                <Link
                  href="mailto:hadleyswello@gmail.com?subject=Privacy Policy Inquiry"
                  color="primary"
                >
                  contact us
                </Link>{" "}
                or reach out through the About page.
              </Typography>
            </section>
          </Box>
        </Paper>
      </Container>
    </Box>
    </>
  );
}

